import { useEffect, useRef, useState } from "react";
import { api, formatApiError, getUpgradeInfo } from "../../lib/api";
import { UpgradeNotice } from "../plan/PlanUI";
import { CHAT } from "../../constants/testIds";
import { X, Loader2, GraduationCap, Check, AlertTriangle, Timer, Lock } from "lucide-react";
import confetti from "canvas-confetti";

const SIMULADO_MIN = 40; // duração do simulado cronometrado (plano Pro)

export function QuizModal({ open, onClose, conversationId, recursos }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [difficulty, setDifficulty] = useState("medio");
  const [nQuestions, setN] = useState(5);
  const [simulado, setSimulado] = useState(false);
  const [deadline, setDeadline] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const submittingRef = useRef(false);

  // Relógio do simulado (só roda com prova aberta e cronometrada)
  useEffect(() => {
    if (!deadline || result) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [deadline, result]);

  const submit = async () => {
    if (submittingRef.current || !quiz) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/submit", { quiz_id: quiz.id, answers });
      setResult(data);
      setDeadline(null);
      if (data.score >= 0.7) {
        confetti({ particleCount: 80, spread: 80, colors: ["#7c3aed", "#a78bfa", "#fbbf24"] });
      }
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
    submittingRef.current = false;
  };

  // Tempo esgotado → corrige automático com o que foi respondido
  const msLeft = deadline ? Math.max(0, deadline - now) : null;
  useEffect(() => {
    if (open && msLeft === 0 && quiz && !result) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, msLeft === 0, quiz, result]);

  const simuladoLocked = recursos && !recursos.simulado;

  if (!open) return null;

  const generate = async () => {
    setBusy(true); setErr(null); setUpgrade(null); setResult(null); setAnswers({}); setDeadline(null);
    try {
      const { data } = await api.post("/quiz/generate", {
        conversation_id: conversationId,
        difficulty,
        n_questions: simulado ? Math.max(nQuestions, 12) : nQuestions,
        simulado,
      });
      setQuiz(data);
      if (data.simulado) setDeadline(Date.now() + SIMULADO_MIN * 60000);
    } catch (e) {
      const up = getUpgradeInfo(e);
      if (up) setUpgrade(up);
      else setErr(formatApiError(e));
    }
    setBusy(false);
  };

  const reset = () => { setQuiz(null); setResult(null); setAnswers({}); setErr(null); setDeadline(null); };

  const mm = msLeft != null ? Math.floor(msLeft / 60000) : 0;
  const ss = msLeft != null ? Math.floor((msLeft % 60000) / 1000) : 0;

  const toggleSimulado = () => {
    if (simuladoLocked) {
      setUpgrade({
        recurso: "simulado",
        plano_necessario: "pro",
        mensagem: `O Simulado cronometrado de ${SIMULADO_MIN} min faz parte do plano Pro. Faça upgrade pra treinar como na prova de verdade ⏱️`,
      });
      return;
    }
    setSimulado((s) => !s);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid={CHAT.quizModal} className="w-full sm:max-w-2xl bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-violet-400" />
            <h2 className="font-bold tracking-tight">{quiz?.simulado ? "Simulado" : "Quiz"}</h2>
            {deadline && !result && (
              <span
                data-testid="simulado-timer"
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${
                  msLeft < 5 * 60000 ? "border-red-500/50 bg-red-500/10 text-red-300" : "border-violet-500/50 bg-violet-500/10 text-violet-300"
                }`}
              >
                <Timer className="h-3 w-3" /> {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
              </span>
            )}
          </div>
          <button onClick={() => { reset(); onClose(); }} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin">
          {!quiz && (
            <div className="space-y-3">
              {upgrade && <UpgradeNotice info={upgrade} onDismiss={() => setUpgrade(null)} />}
              <p className="text-sm text-muted-foreground">
                Vai gerar baseado no material e no contexto desta conversa. Sem inventar.
              </p>
              <div className="flex gap-2">
                {["facil", "medio", "dificil"].map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`flex-1 rounded-full text-xs py-2 capitalize border transition ${
                      difficulty === d ? "border-violet-500 bg-violet-500/10 text-violet-300" : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >{d === "facil" ? "fácil" : d}</button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Quantas</span>
                <input type="range" min={3} max={simulado ? 20 : 10} value={nQuestions} onChange={(e) => setN(parseInt(e.target.value, 10))} className="flex-1 accent-violet-500" />
                <span className="text-sm font-medium w-6">{nQuestions}</span>
              </div>
              {/* Simulado cronometrado — recurso Pro; bloqueado aparece com cadeado + CTA */}
              <button
                type="button"
                data-testid="simulado-toggle"
                onClick={toggleSimulado}
                className={`w-full flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm text-left transition ${
                  simulado
                    ? "border-violet-500 bg-violet-500/10 text-violet-300"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <Timer className="h-4 w-4 shrink-0" />
                <span className="flex-1">
                  <span className="font-medium block">Simulado cronometrado · {SIMULADO_MIN} min</span>
                  <span className="text-xs">Mais questões, relógio correndo e correção automática no fim — como na prova.</span>
                </span>
                {simuladoLocked && <Lock className="h-4 w-4 text-violet-400 shrink-0" />}
                {simulado && !simuladoLocked && <Check className="h-4 w-4 shrink-0" />}
              </button>
              <button
                data-testid={CHAT.quizGenerate}
                onClick={generate} disabled={busy}
                className="w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white py-3 text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {simulado ? "Começar simulado" : "Gerar quiz"}
              </button>
            </div>
          )}

          {quiz && !result && quiz.questions.map((q, idx) => (
            <div key={q.id} className="rounded-xl border border-border bg-secondary p-4 space-y-2">
              <p className="text-sm font-medium">
                <span className="text-violet-400">{idx + 1}.</span> {q.statement}
              </p>
              <div className="space-y-1.5">
                {Object.entries(q.options).map(([k, v]) => (
                  <label
                    key={k}
                    data-testid={CHAT.quizOption(q.id, k)}
                    className={`flex items-start gap-2 rounded-lg p-2.5 cursor-pointer text-sm border transition ${
                      answers[q.id] === k ? "border-violet-500 bg-violet-500/10" : "border-border hover:bg-muted/60"
                    }`}
                  >
                    <input
                      type="radio"
                      checked={answers[q.id] === k}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: k }))}
                      className="mt-0.5 accent-violet-500"
                    />
                    <span><strong className="text-violet-400 mr-1">{k.toUpperCase()})</strong>{v}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {result && (
            <div className="space-y-4">
              <div className={`rounded-xl p-5 text-center ${result.score >= 0.7 ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-amber-500/10 border border-amber-500/30"}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Resultado</p>
                <p className="text-3xl font-bold mt-1">{result.correct}/{result.total}</p>
                <p className="text-sm text-muted-foreground mt-1">{Math.round(result.score * 100)}% de acertos</p>
              </div>
              {quiz.questions.map((q, idx) => {
                const det = result.detail.find((d) => d.question_id === q.id);
                return (
                  <div key={q.id} className={`rounded-xl border p-4 space-y-2 ${det.is_correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}>
                    <div className="flex items-start gap-2">
                      {det.is_correct ? <Check className="h-4 w-4 text-emerald-400 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5" />}
                      <p className="text-sm font-medium flex-1">{idx + 1}. {q.statement}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sua resposta: <strong className={det.is_correct ? "text-emerald-300" : "text-red-300"}>{(det.chosen || "—").toUpperCase()}</strong>
                      {!det.is_correct && <> · Correta: <strong className="text-emerald-300">{det.correct.toUpperCase()}</strong></>}
                    </p>
                    <p className="text-sm text-foreground/90">{q.explanation}</p>
                  </div>
                );
              })}
              <button onClick={reset} className="w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white py-3 text-sm font-medium">Gerar outro quiz</button>
            </div>
          )}
          {err && <p className="text-sm text-red-400">{err}</p>}
        </div>

        {quiz && !result && (
          <div className="p-4 border-t border-border">
            <button
              data-testid={CHAT.quizSubmit}
              onClick={submit} disabled={busy || Object.keys(answers).length < quiz.questions.length}
              className="w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white py-3 text-sm font-medium disabled:opacity-50"
            >
              {busy ? "Corrigindo..." : "Corrigir quiz"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
